import axios from 'axios';
import * as securityLogger from '../utils/securityLogger';
import { translate } from '../i18n/translate';

/**
 * Parse JWT token to extract payload
 * @param {string} token - JWT token
 * @returns {object} Decoded payload or null if invalid
 */
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.warn('Failed to parse JWT:', error);
    return null;
  }
}

/**
 * Check if token is expiring soon (within 2 minutes)
 * @param {string} token - JWT token
 * @returns {boolean} True if token expiring soon or invalid
 */
function isTokenExpiringSoon(token) {
  if (!token) return false;
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return false;

  const expiresAt = payload.exp * 1000; // Convert to milliseconds
  const now = Date.now();
  const timeUntilExpiry = expiresAt - now;

  // Refresh if less than 2 minutes remaining
  return timeUntilExpiry < 2 * 60 * 1000;
}

const apiPrefix = process.env.REACT_APP_API_PREFIX || 'api/v1';
const rawBase = process.env.REACT_APP_API_URL || '';
// Empty rawBase = proxy mode: requests go to /api/v1/... and webpack dev server
// proxies them to the backend (configured via "proxy" in package.json).
const baseURL = rawBase
  ? (rawBase.replace(/\/$/, '').endsWith(apiPrefix)
      ? rawBase.replace(/\/$/, '')
      : `${rawBase.replace(/\/$/, '')}/${apiPrefix}`)
  : `/${apiPrefix}`;

/**
 * Last-known-good copies of GET responses, with LRU eviction.
 * Pattern: const cacheKey = `${method}:${url}:${JSON.stringify(params)}`
 *
 * Every GET goes to the network and the caller always gets that fresh response;
 * a copy is stored here afterwards (never served instead of a fresh read), so a
 * list a mutation just changed cannot come back stale. Mutations drop the cached
 * entries of the resource they touched (see invalidateAfterWrite).
 *
 * Cache TTL by endpoint:
 * - List endpoints (/courses/admin, /enrollments, /threads): 3 minutes
 * - User profile endpoints: 10 minutes
 * - Standard GET endpoints: 5 minutes
 * - Mutation endpoints (POST, PUT, DELETE): no cache
 *
 * SECURITY: LRU eviction prevents unbounded memory growth
 * Max cache entries: 500 (evicts oldest when exceeded)
 */
const requestCache = new Map();
const MAX_CACHE_SIZE = 500;

/**
 * Determine cache TTL based on URL endpoint
 * @param {string} url - Request URL
 * @returns {number} TTL in milliseconds
 */
function getCacheTTL(url) {
  // List endpoints - 3 minutes
  if (url.includes('/courses/admin') || url.includes('/enrollments') || url.includes('/threads')) {
    return 3 * 60 * 1000;
  }
  // User profile endpoints - 10 minutes
  if (url.includes('/users') || url.includes('/profile') || url.includes('/users/me')) {
    return 10 * 60 * 1000;
  }
  // Standard GET endpoints - 5 minutes
  return 5 * 60 * 1000;
}

/**
 * Generate cache key from request config
 * SECURITY: Sort object keys to ensure deterministic cache keys
 * Prevents cache misses on identical requests with different param ordering
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {object} params - Query parameters
 * @returns {string} Cache key
 */
function generateCacheKey(method, url, params = {}) {
  let paramStr = '';
  if (Object.keys(params).length > 0) {
    // Sort keys alphabetically for deterministic stringification
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {});
    paramStr = JSON.stringify(sortedParams);
  }
  return `${method}:${url}:${paramStr}`;
}

/**
 * Get cached response if available and not expired
 * @param {string} cacheKey - Cache key
 * @returns {object|null} Cached response or null
 */
function getCachedResponse(cacheKey) {
  const cached = requestCache.get(cacheKey);
  if (!cached) return null;

  const now = Date.now();
  if (now - cached.timestamp > cached.ttl) {
    requestCache.delete(cacheKey);
    return null;
  }

  return cached.response;
}

/**
 * Set cache entry with LRU eviction
 * SECURITY: Prevents unbounded cache growth by evicting oldest entries when size exceeds MAX_CACHE_SIZE
 * @param {string} cacheKey - Cache key
 * @param {object} response - Response data
 * @param {number} ttl - Time to live in milliseconds (default 5000ms)
 */
function setCacheEntry(cacheKey, response, ttl = 5000) {
  // Re-adding a key moves it to the newest position and must not evict another entry
  requestCache.delete(cacheKey);

  // SECURITY: LRU eviction - delete oldest entry if at capacity
  if (requestCache.size >= MAX_CACHE_SIZE) {
    const firstKey = requestCache.keys().next().value;
    if (firstKey) {
      requestCache.delete(firstKey);
    }
  }

  requestCache.set(cacheKey, {
    response,
    timestamp: Date.now(),
    ttl
  });
}

/**
 * Invalidate cache entries matching a pattern (used for mutations)
 * @param {string} urlPattern - URL pattern to match (can include wildcards)
 */
function invalidateCache(urlPattern) {
  const pattern = urlPattern.replace(/\*/g, '.*');
  const regex = new RegExp(pattern);

  for (const key of requestCache.keys()) {
    if (regex.test(key)) {
      requestCache.delete(key);
    }
  }
}

/**
 * First path segment of a request URL (relative to the API base) or of a cache
 * key: '/teams/abc/members' -> 'teams', 'GET:/users:{"page":1}' -> 'users'.
 */
function resourceRoot(urlOrKey) {
  return String(urlOrKey || '')
    .replace(/^[A-Z]+:/, '')
    .replace(/^\/+/, '')
    .split(/[/?#:]/)[0];
}

// A write to one resource also changes what other resources report, so their
// cached reads go too: a staff login made through /teams is a row of /users,
// cohort and seat-pack writes change the counts shown on /organizations, and a
// lesson or module write changes the course that lists it.
const RELATED_RESOURCES = {
  teams: ['users'],
  users: ['teams'],
  organizations: ['cohorts', 'seat-packs'],
  cohorts: ['organizations'],
  'seat-packs': ['organizations'],
  enrollments: ['courses', 'teacher', 'organizations'],
  lessons: ['courses', 'modules'],
  modules: ['courses', 'lessons'],
  messages: ['chat', 'notifications'],
};

/**
 * After a successful write, drop every cached read under the resource it
 * touched (the first path segment, so /exams/:id/publish also clears the
 * /exams/admin list) plus the resources that read from it.
 * @param {string} url - URL of the mutation request
 */
function invalidateAfterWrite(url) {
  const root = resourceRoot(url);
  if (!root) {
    requestCache.clear();
    return;
  }
  const roots = new Set([root, ...(RELATED_RESOURCES[root] || [])]);
  for (const key of Array.from(requestCache.keys())) {
    if (roots.has(resourceRoot(key))) {
      requestCache.delete(key);
    }
  }
}

/**
 * SECURITY: Get token from secure storage (sessionStorage for sensitive tokens)
 * Falls back to localStorage for backward compatibility during migration
 * @param {string} key - Token key (accessToken or refreshToken)
 * @returns {string|null} Token value or null
 */
function getTokenFromStorage(key) {
  // SECURITY: Try sessionStorage first (more secure against XSS)
  let token = sessionStorage.getItem(key);

  // TODO: Remove localStorage fallback after 2026-06-01.
  // This is a migration shim for sessions created before sessionStorage-only storage.
  // After the deadline, delete these lines.
  if (!token) {
    token = localStorage.getItem(key);
  }

  return token;
}

/**
 * SECURITY: Store token securely using sessionStorage (XSS-resistant)
 * @param {string} key - Token key
 * @param {string} value - Token value
 */
function storeToken(key, value) {
  if (value == null || value === 'undefined') return;
  // SECURITY: Store in sessionStorage (cleared on browser close)
  sessionStorage.setItem(key, value);

  // Also clear from localStorage to prevent XSS exposure
  localStorage.removeItem(key);
}

/**
 * SECURITY: Clear sensitive tokens and user data
 */
function clearTokens() {
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('csrfToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
}

/**
 * SECURITY: Get CSRF token from session storage
 * @returns {string|null} CSRF token
 */
function getCsrfToken() {
  return sessionStorage.getItem('csrfToken') || null;
}

/**
 * SECURITY: Store CSRF token from response headers
 * @param {string} token - CSRF token from response
 */
function storeCsrfToken(token) {
  if (token) {
    sessionStorage.setItem('csrfToken', token);
  }
}

/**
 * Validates API config. In production, REACT_APP_API_URL must be set.
 * Call early (e.g. index.js) to show a clear message if missing.
 * @returns {string} baseURL
 * @throws {Error} in production when REACT_APP_API_URL is missing
 */
export function ensureApiConfig() {
  // REACT_APP_API_URL is validated at module load time; if we reach here it is set.
  return baseURL;
}

export const apiClient = axios.create({
  baseURL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * RACE CONDITION PREVENTION: Token Refresh Subscriber Pattern
 *
 * Problem: When token expires, multiple concurrent requests may all detect expiry
 * and attempt to refresh simultaneously, creating race conditions.
 *
 * Solution: Subscriber pattern ensures only ONE refresh happens:
 * 1. First request sets isRefreshing=true and calls refreshAccessToken()
 * 2. Other concurrent requests subscribe to the refresh completion
 * 3. Once refresh completes, all subscribers receive the new token
 * 4. All requests then proceed with the fresh token
 * 5. If the refresh fails, every subscriber is rejected, so no request is left
 *    waiting for a token that is never coming
 *
 * This prevents duplicate refresh attempts and ensures all in-flight requests
 * use the same new token, preventing 401 loops.
 */

let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Subscribe to token refresh completion
 * Only called when refresh is already in progress
 * @param {function} onSuccess - Called when refresh completes with new token
 * @param {function} onFailure - Called with the error when the refresh fails
 */
function onRefreshed(onSuccess, onFailure) {
  refreshSubscribers.push({ onSuccess, onFailure });
}

/**
 * Notify all subscribers that token has been refreshed
 * Called exactly once per refresh attempt after new token is stored
 * @param {string} token - New access token
 */
function notifyRefreshed(token) {
  // Take the list first so a callback that subscribes again starts a new cycle
  const waiting = refreshSubscribers;
  refreshSubscribers = [];
  waiting.forEach(({ onSuccess }) => onSuccess(token));
}

/**
 * Notify all subscribers that the refresh failed and drop them
 * @param {Error} error - Why the refresh failed
 */
function notifyRefreshFailed(error) {
  const waiting = refreshSubscribers;
  refreshSubscribers = [];
  waiting.forEach(({ onFailure }) => onFailure(error));
}

/**
 * Refresh the access token
 * @returns {Promise} Resolves with new tokens or rejects if refresh fails
 */
async function refreshAccessToken() {
  try {
    const refreshToken = getTokenFromStorage('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // A timeout keeps one hung refresh from holding every queued request forever.
    const response = await axios.post(`${baseURL}/auth/token/refresh`, {
      refreshToken,
    }, { timeout: 15000 });

    const data = response.data;
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid refresh token response');
    }
    const { accessToken: newAccessToken, refreshToken: newRefreshToken } = data;

    storeToken('accessToken', newAccessToken);
    storeToken('refreshToken', newRefreshToken);

    // SECURITY: Extract and store CSRF token from response headers if present
    if (response.headers['x-csrf-token']) {
      storeCsrfToken(response.headers['x-csrf-token']);
    }

    // SECURITY: Log token refresh
    securityLogger.logTokenRefresh();

    return newAccessToken;
  } catch (error) {
    // Only an *invalid* refresh token ends the session. Transient failures
    // (rate limiting, 5xx, network blips) must not log the user out — the
    // next request simply retries the refresh.
    const status = error?.response?.status;
    const tokenRejected = status === 400 || status === 401 || status === 403 || !getTokenFromStorage('refreshToken');
    if (tokenRejected) {
      clearTokens();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    throw error;
  }
}

/**
 * SECURITY: Request interceptor
 * - Injects authorization header from secure storage
 * - Implements proactive token refresh
 * - Adds CSRF token for state-changing requests
 */
apiClient.interceptors.request.use(
  async (config) => {
    const accessToken = getTokenFromStorage('accessToken');

    // Check if token is expiring soon and refresh proactively
    if (accessToken && isTokenExpiringSoon(accessToken)) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          notifyRefreshed(newToken);
          config.headers.Authorization = `Bearer ${newToken}`;
        } catch (error) {
          isRefreshing = false;
          // Requests parked behind this refresh must fail too, not wait forever
          notifyRefreshFailed(error);
          return Promise.reject(error);
        }
      } else {
        // If already refreshing, wait for completion
        return new Promise((resolve, reject) => {
          onRefreshed((token) => {
            config.headers.Authorization = `Bearer ${token}`;
            resolve(config);
          }, reject);
        });
      }
    } else if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // SECURITY: Add CSRF token for state-changing requests (POST, PUT, DELETE, PATCH)
    const method = config.method?.toUpperCase();
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        // Use double-submit cookie pattern: CSRF token in header
        config.headers['X-CSRF-Token'] = csrfToken;
      } else {
        // TODO: Backend should send CSRF token in response headers during login
        // FALLBACK: Log warning for debugging CSRF token availability
        console.warn(
          'CSRF token not available for ' + method + ' request to ' + config.url +
          '. Backend should provide X-CSRF-Token in response headers during login or initial request.'
        );
        // Continue request anyway - backend may have CSRF protection via SameSite cookies
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Statuses the user cannot act on get one friendly, translated sentence
// (shell.errors.*) instead of the proxy's or the guard's wording.
const FRIENDLY_ERROR_KEYS = {
  429: 'rateLimited',
  502: 'serverUnavailable',
  503: 'serverUnavailable',
  504: 'timeout',
};

// What the backend's guards say when they refuse without a reason of their own.
const GENERIC_FORBIDDEN = /^(access denied|forbidden( resource)?)\.?$/i;
// A 404 that names nothing the user could act on: Nest's "Not Found" / "Cannot GET /x", or the
// services' "<Entity> not found".
const GENERIC_NOT_FOUND = /(^not found\.?$)|(^cannot (get|post|put|patch|delete) )|(not found\.?$)/i;
// class-validator's developer wording ("title should not be empty", "property x should not exist").
const VALIDATION_TEXT = /\b(should not exist|should not be empty|must be (a|an|one of|longer|shorter|shorter or equal|longer or equal|less|greater|before|after)\b|must match|must not be)/i;
const ALREADY_EXISTS = /\b(already exists?|already registered|already in use|already taken|duplicate)\b/i;

/**
 * The shell wording for a failure, or null when the backend said something specific that a page
 * may want to show (its pages still prefer their own translated fallback: utils/errorText). The
 * backend speaks English and a 500 may carry raw driver text, so those never reach the screen.
 */
function friendlyErrorKey(status, sent, sentText) {
  const known = FRIENDLY_ERROR_KEYS[status];
  if (known) return known;
  if (status === 403 && (!sentText || GENERIC_FORBIDDEN.test(sentText))) return 'forbidden';
  if (status >= 500) return 'serverError';
  if (status === 404 && (!sentText || GENERIC_NOT_FOUND.test(sentText))) return 'notFound';
  if (status === 400 && (Array.isArray(sent) || (sentText && VALIDATION_TEXT.test(sentText)))) return 'invalidInput';
  if ((status === 409 || status === 400) && sentText && ALREADY_EXISTS.test(sentText)) return 'alreadyExists';
  return null;
}

/**
 * Turn a failed request into the plain object pages handle: { message, code, ...body }.
 * `code` is the HTTP status (undefined when the server never answered). Our own
 * wording is already in the UI language and carries `messageKey`; anything the
 * backend said specifically passes through as it sent it.
 * @param {Error} error - axios error (or the error a failed token refresh threw)
 * @returns {{ message: string, messageKey?: string, code?: number }}
 */
function toApiError(error) {
  const status = error.response?.status;
  const data = error.response?.data;
  const body = data && typeof data === 'object' ? data : {};
  const sent = body.error?.message || body.message;
  const sentText = Array.isArray(sent) ? sent.join(', ') : sent;

  let key = status ? friendlyErrorKey(status, sent, sentText) : null;
  if (!key && !error.response) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') key = 'timeout';
    else if (error.code === 'ERR_NETWORK') key = 'network';
  }

  const message = key
    ? translate(`shell.errors.${key}`)
    : sentText || error.message || translate('shell.errors.requestFailed', 'Request failed');
  return { ...body, message, ...(key ? { messageKey: `shell.errors.${key}` } : {}), code: status };
}

/**
 * Response interceptor - handles success responses
 */
apiClient.interceptors.response.use(
  (response) => {
    // Unwrap backend envelope: { success, data, timestamp } -> { data: actualData }
    if (response.data && response.data.success !== undefined && 'data' in response.data) {
      response.data = response.data.data;
    }

    // SECURITY: Extract and store CSRF token from response headers if present
    if (response.headers['x-csrf-token']) {
      storeCsrfToken(response.headers['x-csrf-token']);
    }

    // Store the fresh body of every successful GET (endpoint-specific TTL). The
    // caller gets this fresh response; an older cached copy never replaces it.
    if (response.config.method?.toUpperCase() === 'GET') {
      const cacheKey = generateCacheKey('GET', response.config.url, response.config.params);
      const ttl = getCacheTTL(response.config.url);
      setCacheEntry(cacheKey, response.data, ttl);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 429 Too Many Requests — the API rate-limits bursts, and a page
    // load fans out several parallel GETs. Back off (honouring Retry-After)
    // and retry idempotent GETs up to twice before surfacing an error.
    if (error.response?.status === 429 && originalRequest
        && String(originalRequest.method || 'get').toLowerCase() === 'get') {
      const attempt = originalRequest._rateLimitRetries || 0;
      if (attempt < 2) {
        originalRequest._rateLimitRetries = attempt + 1;
        const retryAfter = Number(error.response.headers?.['retry-after']);
        const delay = Number.isFinite(retryAfter) && retryAfter > 0
          ? Math.min(retryAfter * 1000, 5000)
          : 700 * (attempt + 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return apiClient(originalRequest);
      }
    }

    // Handle 401 Unauthorized - attempt token refresh (skip for auth endpoints)
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');
    if (error.response?.status === 401 && originalRequest && !originalRequest._retried && !isAuthEndpoint) {
      originalRequest._retried = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          notifyRefreshed(newToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          // refreshAccessToken() has already ended the session (tokens cleared,
          // user sent to /login) when the refresh token was rejected (400, 401,
          // 403). Any other failure (429, 5xx, network) keeps the session: fail
          // this request and the ones queued behind it, and let the next call
          // retry the refresh.
          notifyRefreshFailed(refreshError);
          return Promise.reject(toApiError(refreshError));
        }
      } else {
        // If already refreshing, wait for completion and retry
        return new Promise((resolve, reject) => {
          onRefreshed(
            (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              apiClient(originalRequest).then(resolve).catch(reject);
            },
            (refreshError) => reject(toApiError(refreshError)),
          );
        });
      }
    }

    // Handle 403 Forbidden — user is authenticated but lacks permission.
    // Do not redirect to login; dispatch a custom event so the UI can show feedback.
    if (error.response?.status === 403) {
      window.dispatchEvent(new CustomEvent('api:forbidden', {
        detail: { url: error.config?.url }
      }));
    }

    return Promise.reject(toApiError(error));
  }
);

/**
 * On successful mutations, invalidate related cache entries
 */
apiClient.interceptors.response.use(
  (response) => {
    const method = response.config.method?.toUpperCase();

    // Invalidate cache on mutations (POST, PUT, DELETE, PATCH)
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      invalidateAfterWrite(response.config.url);
    }

    return response;
  },
  (error) => Promise.reject(error)
);

export function getApiBaseURL() {
  return baseURL;
}

/**
 * Export cache utilities for use in custom hooks
 */
export { generateCacheKey, getCachedResponse, setCacheEntry, invalidateCache };

/**
 * Export token refresh function and storage utilities for external use
 */
export { refreshAccessToken, isTokenExpiringSoon, parseJwt, getTokenFromStorage, storeToken, clearTokens };
